/* eslint @typescript-eslint/naming-convention: 0 */

import {
	THEME_BG_DEFAULT,
	THEME_BG_PAPER,
	THEME_BORDER_RADIUS,
	THEME_DIVIDER,
	THEME_FONT_FAMILY,
	THEME_PRIMARY_DARK,
	THEME_PRIMARY_LIGHT,
	THEME_PRIMARY_MAIN,
	THEME_SECONDARY_DARK,
	THEME_SECONDARY_LIGHT,
	THEME_SECONDARY_MAIN,
	THEME_TEXT_PRIMARY,
	THEME_TEXT_SECONDARY,
} from "@Common/constants";
import {
	alpha,
	createTheme,
	type PaletteOptions,
	type Theme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import createPalette from "@mui/material/styles/createPalette";
import type { RaThemeOptions } from "react-admin";

// NOTE: I'm using stuff from https://github.com/marmelab/ra-enterprise-demo/blob/master/src/layout/themes.ts
// NOTE: I had to customize some stuff because I'm using base source code from https://marmelab.com/react-admin-demo
// and theme from the ra-enterprise demo

const defaultThemeInvariants = {
	MuiAutocomplete: {
		defaultProps: {
			fullWidth: true,
		},
		variants: [
			{
				props: {},
				style: ({ theme }: Readonly<{ theme: Theme }>) => ({
					[theme.breakpoints.down("sm")]: { width: "100%" },
				}),
			},
		],
	},
	MuiTextField: {
		defaultProps: {
			variant: "filled" as const,
			margin: "dense" as const,
			size: "small" as const,
			fullWidth: true,
		},
		variants: [
			{
				props: {},
				style: ({ theme }: Readonly<{ theme: Theme }>) => ({
					[theme.breakpoints.down("sm")]: { width: "100%" },
				}),
			},
		],
	},
	MuiFormControl: {
		defaultProps: {
			variant: "filled" as const,
			margin: "dense" as const,
			size: "small" as const,
			fullWidth: true,
		},
	},
	RaSimpleFormIterator: {
		defaultProps: {
			fullWidth: true,
		},
	},
	RaTranslatableInputs: {
		defaultProps: {
			fullWidth: true,
		},
	},
	// Make list actions bar smaller
	RaList: {
		styleOverrides: {
			root: {
				"& .RaList-actions": {
					height: "30px",
				},
			},
		},
	},
	// Make top Appbar smaller
	MuiToolbar: {
		styleOverrides: {
			root: {
				height: "30px",
			},
		},
	},
};

const darkPalette = createPalette({
	mode: "dark",
	primary: {
		dark: "#0761d1",
		main: "#0070f3",
		light: "#3291ff",
		contrastText: "#fff",
	},
	secondary: {
		dark: "#4c2889",
		main: "#7928ca",
		light: "#8a63d2",
		contrastText: "#fff",
	},
	background: {
		paper: "#111111",
		default: "#000000",
	},
	text: {
		primary: "#ededed",
		secondary: "#888888",
	},
	divider: "#333333",
});

const darkComponentsOverrides = (theme: Readonly<Theme>) => ({
	...defaultThemeInvariants,
	RaLayout: {
		styleOverrides: {
			root: {
				"& .RaLayout-content": {
					padding: theme.spacing(1),
					[theme.breakpoints.up("md")]: {
						padding: theme.spacing(2),
					},
				},
			},
		},
	},
	MuiAppBar: {
		styleOverrides: {
			root: {
				// Hide MenuItemCategory shadow behind the appbar
				zIndex: 9999,
			},
		},
	},
	// This part screwed up the menu drawer
	// RaAppBar: {
	//   styleOverrides: {
	//     root: {
	//       backgroundColor: darkPalette.background.paper,
	//       color: "#fff",
	//       "& .RaAppBar-menuButton": {
	//         // Since sub-<Menu /> hide labels when sidebar is closed
	//         // We need to disallow sidebar closing on desktop (hiding button is simpler)
	//         display: "block",
	//         [theme.breakpoints.up("md")]: {
	//           display: "none",
	//         },
	//       },
	//     },
	//   },
	// },
	RaSearchInput: {
		styleOverrides: {
			root: {
				color: darkPalette.common.white,
				backgroundColor: alpha(darkPalette.common.black, 0.04),
				"&:hover": {
					backgroundColor: alpha(darkPalette.common.black, 0.13),
				},
				"&:focus": {
					backgroundColor: alpha(darkPalette.common.black, 0.13),
				},
				"&:focus-within": {
					backgroundColor: alpha(darkPalette.common.black, 0.13),
				},
				"& .RaSearchInput-inputBase": {
					background: alpha(darkPalette.common.black, 0.04),
					"&:hover": {
						background: alpha(darkPalette.common.black, 0.1),
					},
				},
				"& .RaSearchInput-inputAdornmentStart": {
					color: darkPalette.common.white,
				},
			},
		},
	},
	MuiPaper: {
		styleOverrides: {
			root: {
				border: "1px solid #333333",
				background: darkPalette.background.paper,
			},
		},
	},
	MuiCard: {
		styleOverrides: {
			root: {
				// Required for cards not to be transparent and show Retrowave background through
				/*
				Context:
				The problem was a CSS stacking issue: the RetrowaveBackground (position: fixed, zIndex: 0) was painting on top of the
  non-positioned Card. Adding position: relative and zIndex: 1 to the wrapper Box lifts the content above the background canvas.
   The Card should now be visible with 0.9 opacity.
				*/
				backgroundColor: "rgba(0, 0, 0, 0.7)",
				position: "relative" as const,
				zIndex: 1,
			},
		},
	},
	RaToolbar: {
		styleOverrides: {
			root: () => ({
				position: "static",
				backgroundColor: "transparent",
				boxShadow: "none",
				borderTop: "none",
				paddingLeft: 0,
				paddingRight: 0,
			}),
		},
	},
});

const lightPalette = createPalette({
	mode: "light",
	primary: {
		dark: THEME_PRIMARY_DARK,
		main: THEME_PRIMARY_MAIN,
		light: THEME_PRIMARY_LIGHT,
		contrastText: "#fff",
	},
	secondary: {
		dark: THEME_SECONDARY_DARK,
		main: THEME_SECONDARY_MAIN,
		light: THEME_SECONDARY_LIGHT,
		contrastText: "#fff",
	},
	background: {
		paper: THEME_BG_PAPER,
		default: THEME_BG_DEFAULT,
	},
	text: {
		primary: THEME_TEXT_PRIMARY,
		secondary: THEME_TEXT_SECONDARY,
	},
	divider: THEME_DIVIDER,
});

const lightComponentsOverrides = (theme: Readonly<Theme>) => ({
	...defaultThemeInvariants,
	RaLayout: {
		styleOverrides: {
			root: {
				"& .RaLayout-content": {
					padding: theme.spacing(1),
					[theme.breakpoints.up("md")]: {
						padding: theme.spacing(2),
					},
				},
			},
		},
	},
	// This part screwed up the menu drawer
	// RaAppBar: {
	//   styleOverrides: {
	//     root: {
	//       "& .RaAppBar-menuButton": {
	//         // Since sub-<Menu /> hide labels when sidebar is closed
	//         // We need to disallow sidebar closing on desktop (hiding button is simpler)
	//         display: "block",
	//         [theme.breakpoints.up("md")]: {
	//           display: "none",
	//         },
	//       },
	//     },
	//   },
	// },
	RaSearchInput: {
		styleOverrides: {
			root: {
				color: lightPalette.text.primary,
				backgroundColor: alpha(lightPalette.common.black, 0.04),
				"&:hover": {
					backgroundColor: alpha(lightPalette.common.black, 0.13),
				},
				"&:focus": {
					backgroundColor: alpha(lightPalette.common.black, 0.13),
				},
				"&:focus-within": {
					backgroundColor: alpha(lightPalette.common.black, 0.13),
				},
				"& .RaSearchInput-inputBase": {
					borderRadius: 10,
					background: alpha(lightPalette.common.black, 0.04),
					"&:hover": {
						background: alpha(lightPalette.common.black, 0.1),
					},
				},
				"& .RaSearchInput-inputAdornmentStart": {
					color: alpha("#000000", 0.38),
				},
			},
		},
	},
	RaMenuItemLink: {
		styleOverrides: {
			root: {
				borderLeft: "3px solid #fff",
				"& .RaMenuItemLink-active": {
					borderLeft: "3px solid #808080",
				},
			},
		},
	},
	RaMenuItemCategory: {
		styleOverrides: {
			root: {
				"& .RaMenuItemCategory-container": {
					color: "#808080",
					"&:hover": {
						color: "black",
						backgroundColor: grey[200],
					},
				},
				"& .RaMenuItemCategory-popoverPaper": {
					boxShadow: theme.shadows[2],
					backgroundColor: lightPalette.background.paper,
				},
			},
		},
	},
	RaMenuItem: {
		styleOverrides: {
			root: {
				color: "#808080",
			},
		},
	},
	MuiPaper: {
		styleOverrides: {
			elevation1: {
				boxShadow: "none",
			},
			root: {
				border: "1px solid #eaeaea",
				backgroundClip: "padding-box",
			},
		},
	},
	MuiCard: {
		styleOverrides: {
			root: {
				// Required for cards not to be transparent and show Retrowave background through
				/*
				Context:
				The problem was a CSS stacking issue: the RetrowaveBackground (position: fixed, zIndex: 0) was painting on top of the
  non-positioned Card. Adding position: relative and zIndex: 1 to the wrapper Box lifts the content above the background canvas.
   The Card should now be visible with 0.9 opacity.
				*/
				backgroundColor: "rgba(255, 255, 255, 0.6)",
				position: "relative" as const,
				zIndex: 1,
			},
		},
	},
	MuiButton: {
		styleOverrides: {
			contained: {
				boxShadow: "none",
				backgroundColor: "#111111",
				color: "#ffffff",
				"&:hover": {
					backgroundColor: "#333333",
					boxShadow: "none",
				},
			},
			outlined: {
				borderColor: "#eaeaea",
				color: "#111111",
				"&:hover": {
					borderColor: "#111111",
					backgroundColor: "transparent",
				},
			},
		},
	},
	MuiAppBar: {
		styleOverrides: {
			// Hide MenuItemCategory shadow behind the appbar
			root: { zIndex: 9999 },
			colorSecondary: {
				color: "#111111",
				backgroundColor: "#fff",
			},
		},
	},
	MuiLinearProgress: {
		styleOverrides: {
			colorPrimary: {
				backgroundColor: "#f5f5f5",
			},
			barColorPrimary: {
				backgroundColor: "#d7d7d7",
			},
		},
	},

	MuiFilledInput: {
		styleOverrides: {
			root: {
				backgroundColor: "rgba(0, 0, 0, 0.04)",
				"&$disabled": {
					backgroundColor: "rgba(0, 0, 0, 0.04)",
				},
			},
		},
	},
	// RaSidebar: {
	//     styleOverrides: {
	//         root: {
	//             '& .RaSidebar-drawerPaper': {},
	//             '& .RaSidebar-fixed': {
	//                 zIndex: 1200,
	//                 width: 76,
	//             },
	//             '&.MuiDrawer-docked .MuiPaper-root': {
	//                 width: 76,
	//             },
	//         },
	//     },
	// },
	RaLinkedData: {
		styleOverrides: {
			root: {
				"&:hover": {
					backgroundColor: "#ddd",
				},
			},
		},
	},
	RaToolbar: {
		styleOverrides: {
			root: () => ({
				position: "static",
				backgroundColor: "transparent",
				boxShadow: "none",
				borderTop: "none",
				paddingLeft: 0,
				paddingRight: 0,
			}),
		},
	},
});

const createSoftTheme = (
	palette: Readonly<RaThemeOptions["palette"]>,
	// biome-ignore lint/suspicious/noExplicitAny: ignore
	componentsOverrides: (_theme: Readonly<Theme>) => any,
) => {
	const themeOptions = {
		palette: palette as PaletteOptions,
		shape: {
			borderRadius: Number.parseInt(THEME_BORDER_RADIUS, 10),
		},
		typography: {
			fontFamily: THEME_FONT_FAMILY,
			h1: {
				fontSize: "3.5rem",
				fontWeight: 700,
				lineHeight: 1.2,
			},
			h2: {
				fontSize: "2.5rem",
				fontWeight: 600,
				lineHeight: 1.3,
			},
			h3: {
				fontSize: "2rem",
				fontWeight: 600,
				lineHeight: 1.4,
			},
			h4: {
				fontSize: "1.5rem",
				fontWeight: 600,
				lineHeight: 1.4,
			},
			h5: {
				fontSize: "1.25rem",
				fontWeight: 600,
				lineHeight: 1.5,
			},
			h6: {
				fontSize: "1rem",
				fontWeight: 600,
				lineHeight: 1.5,
			},
			body1: {
				fontSize: "1rem",
				lineHeight: 1.7,
			},
			body2: {
				fontSize: "0.875rem",
				lineHeight: 1.6,
			},
		},
	};
	const theme = createTheme(themeOptions);
	theme.components = componentsOverrides(theme);
	return theme;
};

export const softLightTheme = createSoftTheme(
	lightPalette,
	lightComponentsOverrides,
);
export const softDarkTheme = createSoftTheme(
	darkPalette,
	darkComponentsOverrides,
);
